import React from 'react';
import { ResultInterpretation } from '../../types/tests';
import './ResultScale.css';

interface ResultScaleProps {
    score: number;
    minScore: number;
    maxScore: number;
    interpretations: ResultInterpretation[];
    higherIsBetter?: boolean;
}

const ResultScale: React.FC<ResultScaleProps> = ({ score, minScore, maxScore, interpretations, higherIsBetter = false }) => {
    // Calculate percentage position of the score
    const scorePercentage = ((score - minScore) / (maxScore - minScore)) * 100;

    // Color interpolation: first = green, last = red, middle = interpolated
    const getSegmentColor = (index: number, total: number): string => {
        if (total === 1) return '#4CAF50'; // Single segment is green

        // Progress from 0 (green) to 1 (red)
        const progress = index / (total - 1);

        // Define key colors along the spectrum
        const colors = [
            { r: 76, g: 175, b: 80 },   // Green (#4CAF50)
            { r: 255, g: 193, b: 7 },   // Yellow (#FFC107)
            { r: 255, g: 152, b: 0 },   // Orange (#FF9800)
            { r: 244, g: 67, b: 54 },   // Red (#F44336)
        ];

        // Calculate which color segment we're in
        const segmentSize = 1 / (colors.length - 1);
        const segmentIndex = Math.min(Math.floor(progress / segmentSize), colors.length - 2);
        const segmentProgress = (progress - (segmentIndex * segmentSize)) / segmentSize;

        // Interpolate between the two colors
        const startColor = colors[segmentIndex];
        const endColor = colors[segmentIndex + 1];

        const r = Math.round(startColor.r + (endColor.r - startColor.r) * segmentProgress);
        const g = Math.round(startColor.g + (endColor.g - startColor.g) * segmentProgress);
        const b = Math.round(startColor.b + (endColor.b - startColor.b) * segmentProgress);

        return `rgb(${r}, ${g}, ${b})`;
    };

    return (
        <div className="result-scale">
            <div className="scale-labels">
                <span className="scale-label-min">{minScore}</span>
                <span className="scale-label-max">{maxScore}</span>
            </div>

            <div className="scale-bar-container">
                <div className="scale-bar">
                    {interpretations.length > 1 ? (
                        // Show colored segments for tests with multiple interpretations
                        interpretations.map((interp, index) => {
                            const startPercentage = ((interp.minScore - minScore) / (maxScore - minScore)) * 100;
                            const endScore = index < interpretations.length - 1
                                ? interpretations[index + 1].minScore
                                : maxScore;
                            const endPercentage = ((endScore - minScore) / (maxScore - minScore)) * 100;
                            const width = endPercentage - startPercentage;

                            return (
                                <div
                                    key={index}
                                    className="scale-segment"
                                    style={{
                                        left: `${startPercentage}%`,
                                        width: `${width}%`,
                                        backgroundColor: getSegmentColor(index, interpretations.length)
                                    }}
                                    title={interp.category}
                                >
                                    <span className="segment-tooltip">{interp.category}</span>
                                </div>
                            );
                        })
                    ) : (
                        // Show gradient bar for tests without interpretations or with single interpretation
                        // Reverse gradient if higher scores are better
                        <div
                            className="scale-segment"
                            style={{
                                left: '0%',
                                width: '100%',
                                background: higherIsBetter
                                    ? 'linear-gradient(to right, #F44336, #FF9800, #FFC107, #4CAF50)'
                                    : 'linear-gradient(to right, #4CAF50, #FFC107, #FF9800, #F44336)'
                            }}
                        />
                    )}
                </div>

                {/* User score indicator */}
                <div
                    className="score-indicator"
                    style={{ left: `${scorePercentage}%` }}
                >
                    <div className="score-marker" />
                    <div className="score-label">{score}</div>
                </div>
            </div>
        </div>
    );
};

export default ResultScale;
